import React from 'react';
import PropTypes from 'prop-types';
import Select from "react-select";

import * as RowPage from '@components/common/shroud/row_page';
import UGTDisclaimer from '@components/common/language_menu/ugtDisclaimer';
import { determineUGTLocale } from '@components/asset/modal/tabs/edit/helpers';
import {
  getLanguageMapEntryFromSearchString
} from '../user_generated_translations/bulk_ugt_helpers';
import BulkManagementLanguageMenuDropdown from '../user_generated_translations/BulkManagementLanguageMenuDropdown';
import CustomFieldsRow from './custom_fields_row';

import './styles/main.scss';

class CustomFieldsManagement extends React.Component {
  state = {
    visibleKeys: this.props.customFieldKeys,
    queryString: null,
    sortBy: "priority",
    pageSize: this.getPageSize(this.props.customFieldKeys),
    isTranslateMode: false,
  }

  componentDidMount() {
    const { ugtLocaleDefault } = BFG.locales;
    const currentUgtLocale = getLanguageMapEntryFromSearchString()?.locale || determineUGTLocale();
    if (BFG.hasFeature('translations') && currentUgtLocale !== ugtLocaleDefault) {
      this.setState({ isTranslateMode: true });
    }
  }

  getPageSize(updatedKeys) {
    return RowPage.getPageSize(updatedKeys.length);
  }

  updateSearch = (event) => {
    this.setState({ queryString: event.target.value });
  }

  handleSorting = (selection) => {
    this.setState({ sortBy: selection.value });
  }

  updateVisibleKeys = (key, newName) => {
    const { visibleKeys } = this.state;
    let updatedKeys;
    if (newName) {
      const updatedKey = visibleKeys.find((customFieldKey) => customFieldKey.key === key);
      updatedKey.name = newName;
      updatedKeys = Object.assign([], visibleKeys, updatedKey);
    } else {
      updatedKeys = visibleKeys.filter((customFieldKey) => customFieldKey.key !== key);
    }
    const pageSize = this.getPageSize(updatedKeys);
    this.setState({ visibleKeys: updatedKeys, pageSize });
  }

  queriedKeys() {
    const { visibleKeys, queryString } = this.state;
    return visibleKeys.filter((key) => key.name.toLowerCase().includes(queryString.toLowerCase()));
  }

  keysToSort() {
    return this.state.queryString ? this.queriedKeys() : this.state.visibleKeys;
  }

  sortedByPriority() {
    return this.keysToSort().sort((a, b) => {
      if (a.prioritized && b.prioritized) {
        return a.name.toLowerCase() < b.name.toLowerCase() ? -1 : 1;
      }
      if (a.prioritized && !b.prioritized) {
        return -1;
      }
      if (!a.prioritized && !b.prioritized) {
        return a.name.toLowerCase() < b.name.toLowerCase() ? -1 : 1;
      }
      return 1;
    });
  }

  sortedByName() {
    return this.keysToSort().sort((a, b) => (
      (a.name.toLowerCase() < b.name.toLowerCase()) ? -1 : 1));
  }

  sortedByAssetCount() {
    return this.keysToSort().sort((a, b) => (
      (a.counted_assets < b.counted_assets) ? 1 : -1));
  }

  sortedByValueCount() {
    return this.keysToSort().sort((a, b) => (
      (a.uniq_values < b.uniq_values) ? 1 : -1));
  }

  sortedKeys() {
    const { sortBy } = this.state;
    if (sortBy === "name") {
      return this.sortedByName();
    } if (sortBy === "assetCount") {
      return this.sortedByAssetCount();
    } if (sortBy === "valueCount") {
      return this.sortedByValueCount();
    }
    return this.sortedByPriority();
  }

  renderSortingOptions() {
    return (
      <div className="sorting-options">
        <input
          id="custom-fields-search"
          className="bf-input searchbar"
          type="text"
          placeholder={`Search ${this.props.customFieldKeys.length} custom field keys`}
          onChange={this.updateSearch}
        />
        <div className="sorting-options-container-right">
          <BulkManagementLanguageMenuDropdown />
          <div className="sort-options">
            <div><p>{t('Sort by')}:</p></div>
            <Select
              className="select-custom-field-key-sorting"
              closeOnSelect
              onChange={this.handleSorting}
              options={[
                { value: "name", label: t("Name") },
                { value: "assetCount", label: t("Asset Count") },
                { value: "valueCount", label: t("Value Count") },
                { value: "priority", label: t("Priority") }]}
              placeholder={t('Name')}
              value={this.state.sortBy}
              isSearchable={false}
            />
          </div>
        </div>
      </div>
    );
  }

  renderNoCustomFields() {
    return (
      <div className="manage-custom-fields">
        {`${BF.fx.brandfolder().name} ${t('does not have any custom fields')}.`}
      </div>
    );
  }

  renderRows(pageIndex) {
    const pageKeys = RowPage.getPageSlice(pageIndex, this.state.pageSize, this.sortedKeys());
    return pageKeys.map((customFieldKey) => (
      <div className="custom-fields-row" key={customFieldKey.key}>
        <CustomFieldsRow
          customFieldKey={customFieldKey}
          isTranslateMode={this.state.isTranslateMode}
          updateVisibleKeys={this.updateVisibleKeys}
        />
      </div>
    ));
  }

  renderPages() {
    const { pageSize } = this.state;
    const renderedPages = [];
    const sortedKeys = this.sortedKeys();
    const numPages = RowPage.getNumPages(pageSize, sortedKeys.length);
    for (let pageIndex = 0; pageIndex < numPages; pageIndex += 1) {
      renderedPages.push(
        <RowPage.RowPage
          pageSize={pageSize}
          isInitialPage={pageIndex === 0}
          renderRows={() => this.renderRows(pageIndex)}
          key={`custom-fields-row-page-${pageIndex}`}
        />
      );
    }
    return renderedPages;
  }

  render() {
    if (this.props.customFieldKeys.length < 1) {
      return this.renderNoCustomFields();
    }
    return (
      <div className="manage-custom-fields">
        <UGTDisclaimer getSelectedLanguage={getLanguageMapEntryFromSearchString} />
        <h2>{t("Custom Field Keys for")} {BF.fx.brandfolder().name}</h2>
        <hr />
        {this.renderSortingOptions()}
        <div className="headers">
          <div className="headers-container">
            <h3 className="quarter-width">{t('Keys')}</h3>
            <h3 className="fifteen-width"># {t('Values')}</h3>
            <h3 className="fifteen-width"># {t('Assets')}</h3>
            <h3 className="fifteen-width">{t('Prioritized')}</h3>
          </div>
        </div>
        <div className="custom-fields-list">
          { this.renderPages() }
        </div>
      </div>
    );
  }
}

CustomFieldsManagement.propTypes = {
  customFieldKeys: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string,
    counted_assets: PropTypes.number,
    uniq_values: PropTypes.number
  })).isRequired
};

export default CustomFieldsManagement;
